import { useDispatch, useSelector } from '@redux';
import { Col, Form, Modal, Row, Select } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { DynamicObjectType } from 'types/commons';
import crudActions from '@redux/crudActions';
import { RestInputContext } from 'components/form/RestInputContext';
import FormItem from 'components/form/FormItem';
import { getAllLocations } from '@redux/config/actions';
import { getConfigByName } from '@redux/config/selectors';
import { useWatch } from 'antd/es/form/Form';
import { editPickupInformation } from '@redux/ordersTabTryBuy/actions';

const EditPickupModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const provinceId = useWatch('provinceId', form);
  const districtId = useWatch('districtId', form);

  const record = useSelector<DynamicObjectType>(
    (state) => state.ordersTabTryBuy.currentData,
  );
  const modal = useSelector<DynamicObjectType>((state) => state.modal.modal);
  const addressConfig = useSelector((state) => state.config.address) as any;
  const channelLocations = useSelector<DynamicObjectType>((state) =>
    getConfigByName(state, 'channelLocations'),
  );

  const [loading, setLoading] = useState(false);

  const onCancel = () => {
    dispatch(
      crudActions.modal.toogleModal({
        ['editPickup']: { isOpen: false },
      } as any),
    );
  };

  const handleSubmit = useCallback(
    async (values) => {
      setLoading(true);
      const locationId = values?.locationId;
      const res = await dispatch(
        editPickupInformation({
          data: {
            id: record?.id,
            data: {
              locationId,
            },
          },
          options: {},
        }),
      );
      setLoading(false);
      await form.resetFields();
      await onCancel();
      if (res?.payload?.id) {
        window.location.reload();
      }
    },
    [record?.id],
  );

  const onChangeCity = () => {
    form.setFields([
      {
        name: 'districtId',
        value: null,
      },
      {
        name: 'locationId',
        value: null,
      },
    ]);
  };

  const onChangeDistrict = () => {
    form.setFields([
      {
        name: 'locationId',
        value: null,
      },
    ]);
  };

  const items = useMemo(() => {
    if (!channelLocations?.length) return [];

    return channelLocations
      ?.filter(
        (location) =>
          location?.isActive &&
          location?.isTabTryBuy &&
          location?.provinceId === provinceId &&
          location?.districtId === districtId,
      )
      ?.map((location) => ({
        value: location?.id,
        label:
          `${get(location, 'code', '')} - ${get(location, 'name', '')}`.trim(),
      }))
      ?.filter((item) => item?.label);
  }, [channelLocations, provinceId, districtId]);

  const filterSort = useCallback((optionA, optionB) => {
    return (optionA?.label ?? '')
      .toLowerCase()
      .localeCompare((optionB?.label ?? '').toLowerCase());
  }, []);

  useEffect(() => {
    if (modal?.editPickup?.isOpen) {
      dispatch(
        getAllLocations({
          data: {
            filters: [],
          },
        }),
      );
    }
  }, [modal?.editPickup?.isOpen]);

  return (
    <Modal
      open={modal?.editPickup?.isOpen}
      onCancel={onCancel}
      onOk={() => {
        form.submit();
      }}
      okButtonProps={{
        loading,
      }}
      title={t('ordersTabTryBuy.editPickupInfo')}
    >
      <RestInputContext.Provider value={{ form }}>
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          initialValues={{
            ...record?.location,
            locationId: record?.location?.id,
          }}
        >
          <Row gutter={[10, 10]}>
            <Col span={12}>
              <FormItem
                name="provinceId"
                required
                label={t('ordersTabTryBuy.province.label')}
              >
                <Select
                  options={addressConfig?.map((item) => ({
                    label: item?.name,
                    value: item?.id,
                  }))}
                  placeholder={t('ordersTabTryBuy.province.placeholder')}
                  onChange={onChangeCity}
                  allowClear={false}
                  disabled
                />
              </FormItem>
            </Col>
            <Col md={12} sm={24} xs={24}>
              <Form.Item
                noStyle
                shouldUpdate={(p, c) =>
                  get(p, 'provinceId') !== get(c, 'provinceId')
                }
              >
                {({ getFieldValue }) => (
                  <Form.Item
                    label={t('ordersTabTryBuy.district.label')}
                    name="districtId"
                    required
                  >
                    <Select
                      options={addressConfig
                        ?.find(
                          (city) => city.id === getFieldValue('provinceId'),
                        )
                        ?.districts?.map((item) => ({
                          label: item?.name,
                          value: item?.id,
                        }))}
                      placeholder={t('ordersTabTryBuy.district.placeholder')}
                      onChange={onChangeDistrict}
                      allowClear={false}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
            <Col md={24} sm={24} xs={24}>
              <Form.Item
                noStyle
                shouldUpdate={(p, c) =>
                  get(p, 'provinceId') !== get(c, 'provinceId') ||
                  get(p, 'districtId') !== get(c, 'districtId')
                }
              >
                {() => {
                  return (
                    <Form.Item
                      label={t('ordersTabTryBuy.store.label')}
                      name="locationId"
                      required
                    >
                      <Select
                        options={items}
                        filterSort={filterSort}
                        optionFilterProp="label"
                        showSearch
                        placeholder={t('ordersTabTryBuy.district.placeholder')}
                        allowClear
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </RestInputContext.Provider>
    </Modal>
  );
};

export default EditPickupModal;
